/**
 * The world map's geometry: Natural Earth 1:110m countries (world-atlas 2.0.2, public
 * domain), vendored as TopoJSON so the panel needs no map service, projected once with
 * Natural Earth I into a fixed 720 x 360 box. Countries are keyed by ISO alpha-2 like
 * buyer.orders.country_code and growth.geo_cells.country_code.
 */

import { type GeoPermissibleObjects, geoNaturalEarth1, geoPath } from 'd3-geo';
import type { FeatureCollection, Geometry } from 'geojson';
import { feature } from 'topojson-client';
import type { GeometryCollection, Topology } from 'topojson-specification';
import topology from '../assets/countries-110m.json';
import { ISO_NUMERIC_TO_ALPHA2 } from './iso-numeric.js';

export const WORLD_WIDTH = 720;
export const WORLD_HEIGHT = 360;

export interface CountryShape {
  alpha2: string;
  name: string;
  d: string;
  centroid: [number, number];
}

interface World {
  shapes: CountryShape[];
  outline: string;
  project: (lat: number, lon: number) => [number, number] | null;
}

let cache: World | null = null;

type CountriesTopology = Topology<{ countries: GeometryCollection<{ name: string }> }>;

/** Shapes, the sphere outline and a point projector; computed once per page. */
export function world(): World {
  if (cache !== null) return cache;
  const topo = topology as unknown as CountriesTopology;
  const collection = feature(topo, topo.objects.countries) as FeatureCollection<Geometry, { name: string }>;
  const sphere: GeoPermissibleObjects = { type: 'Sphere' };
  const projection = geoNaturalEarth1().fitSize([WORLD_WIDTH, WORLD_HEIGHT], sphere);
  const path = geoPath(projection);
  const shapes: CountryShape[] = [];
  for (const item of collection.features) {
    const d = path(item);
    if (d === null || d === '') continue;
    const numeric = String(item.id ?? '');
    shapes.push({
      alpha2: ISO_NUMERIC_TO_ALPHA2[numeric] ?? numeric,
      name: item.properties?.name ?? numeric,
      d,
      centroid: path.centroid(item),
    });
  }
  cache = {
    shapes,
    outline: path(sphere) ?? '',
    project: (lat, lon) => {
      const point = projection([lon, lat]);
      return point === null ? null : [point[0], point[1]];
    },
  };
  return cache;
}
